import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

const MAX_QTY = 20;

/** @param {HTMLFormElement} form */
function formData(form) {
  return new FormData(form);
}

/**
 * <oppie-pdp-upsell>, de gratis-kiezer plus add-ons in de buy-box.
 *
 * Houdt per gratis-rij en per add-on een aantal bij. Zodra de klant het
 * productformulier verstuurt en er iets gekozen is, onderschept dit element de
 * submit en stuurt hoofdproduct + keuzes in EEN /cart/add-call.
 * LET OP: Horizon registreert zijn `on:submit`-delegatie op `document` in de
 * capture-fase (component.js). Alleen een capture-listener op `window` loopt
 * daar nog vóór. Daarom luisteren we op window en stoppen we de propagatie,
 * anders voegt Horizon het hoofdproduct óók nog een keer toe. Daarna dispatcht het
 * CartAddEvent met de section-HTML zodat drawer, totalen en gratis-verzendbalk
 * via Horizon's morph meebewegen. Zonder keuzes doet Horizon zijn eigen ding.
 */
class OppiePdpUpsell extends HTMLElement {
  connectedCallback() {
    this.baseFreeQty = parseInt(this.dataset.freeQty, 10) || 0;
    this.freeQty = this.baseFreeQty;
    this.moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    this.currency = this.dataset.currency || 'EUR';
    this.rows = Array.from(this.querySelectorAll('[data-upsell-row]'));
    this.addons = Array.from(this.querySelectorAll('[data-upsell-addon]'));
    this.counter = this.querySelector('[data-upsell-counter]');
    this.foot = this.querySelector('[data-upsell-foot]');
    this.errorBox = this.querySelector('[data-upsell-error]');

    this.rows.forEach((row) => this.#bindStepper(row, () => this.#renderFree()));
    this.addons.forEach((addon) => {
      const rerender = () => this.#renderAddon(addon);
      this.#bindStepper(addon, rerender);
      addon.querySelector('[data-upsell-addon-add]')?.addEventListener('click', () => this.#setQty(addon, 1, rerender));
      addon.querySelector('[data-upsell-toggle]')?.addEventListener('click', () => {
        this.#setQty(addon, this.#qty(addon) > 0 ? 0 : 1, rerender);
      });
      rerender();
    });

    this.#rememberInitial();
    this.#renderFree();
    this.#bindForm();
    this.#bindMainQuantity();
  }

  disconnectedCallback() {
    window.removeEventListener('submit', this.#onSubmit, { capture: true });
  }

  #bindStepper(node, onChange) {
    node.querySelector('[data-upsell-minus]')?.addEventListener('click', () => this.#setQty(node, this.#qty(node) - 1, onChange));
    node.querySelector('[data-upsell-plus]')?.addEventListener('click', () => this.#setQty(node, this.#qty(node) + 1, onChange));
  }

  #qty(node) {
    return parseInt(node.dataset.qty, 10) || 0;
  }

  #setQty(node, value, onChange) {
    node.dataset.qty = String(Math.max(0, Math.min(MAX_QTY, value)));
    onChange();
  }

  #money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  /** Het productformulier van dezelfde buy-box. */
  #bindForm() {
    const section = this.closest('.shopify-section') || document;
    this.form = section.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    if (!this.form) return;
    window.addEventListener('submit', this.#onSubmit, { capture: true });
  }

  /** Onthoudt de startstand zodat we na een geslaagde add kunnen resetten. */
  #rememberInitial() {
    [...this.rows, ...this.addons].forEach((node) => {
      node.dataset.initialQty = node.dataset.qty;
    });
  }

  #reset() {
    this.rows.forEach((row) => {
      row.dataset.qty = row.dataset.initialQty || '0';
    });
    // Staat het aantal machines nog op 2+, dan de startkeuze weer meeschalen.
    if (this.freeQty !== this.baseFreeQty) this.#rescaleRows(this.freeQty - this.baseFreeQty, this.freeQty);
    this.addons.forEach((addon) => {
      addon.dataset.qty = addon.dataset.initialQty || '0';
      this.#renderAddon(addon);
    });
    this.#renderFree();
  }

  /**
   * Verdeelt de gratis stuks over de rijen, goedkoopste eerst (zo rekent
   * Shopify's Koop X krijg Y ook), de rest is betaald. Teruggegeven in
   * rijvolgorde zodat de weergave niet verspringt.
   */
  #allocate() {
    let freeLeft = this.freeQty;
    const entries = this.rows.map((row, index) => ({
      row,
      index,
      qty: this.#qty(row),
      price: parseInt(row.dataset.price, 10) || 0,
    }));
    [...entries]
      .sort((a, b) => a.price - b.price || a.index - b.index)
      .forEach((entry) => {
        entry.free = Math.min(entry.qty, freeLeft);
        entry.extra = entry.qty - entry.free;
        freeLeft -= entry.free;
      });
    return entries;
  }

  /**
   * Meer dan één hoofdproduct in het aantal-veld = evenredig meer gratis
   * (de korting geeft N gratis per gekocht hoofdproduct). De gekozen mixen
   * groeien en krimpen mee, anders gaan er bij 2 machines maar 3 mixen mee en
   * vraagt de winkelwagen daarna alsnog om de rest.
   */
  #bindMainQuantity() {
    const input = this.form?.querySelector('input[name="quantity"]');
    if (!input) return;
    const sync = () => {
      const mainQty = Math.max(1, parseInt(input.value, 10) || 1);
      const next = this.baseFreeQty * mainQty;
      if (next === this.freeQty) return;
      this.#rescaleRows(next - this.freeQty, next);
      this.freeQty = next;
      this.#renderFree();
    };
    input.addEventListener('change', sync);
    input.addEventListener('input', sync);
    this.form.addEventListener('click', () => setTimeout(sync, 0));
  }

  /**
   * Schaalt de gekozen aantallen in dezelfde verhouding mee (2 Donker + 1 Licht
   * wordt bij 2 machines 4 + 2). Bij minder machines gaan er alleen gratis
   * stuks af; bewust extra betaalde mixen blijven staan. Alles op nul = de
   * klant wil geen mixen, dan doen we niets.
   */
  #rescaleRows(delta, nextTotal) {
    const current = this.rows.map((row) => this.#qty(row));
    const sum = current.reduce((a, b) => a + b, 0);
    if (sum === 0 || delta === 0) return;
    const target = delta > 0 ? sum + delta : sum - Math.min(-delta, Math.max(0, sum - nextTotal));
    if (target === sum) return;

    const exact = current.map((q) => (q * target) / sum);
    const floored = exact.map(Math.floor);
    const short = target - floored.reduce((a, b) => a + b, 0);
    const bonus = exact
      .map((value, index) => ({ index, rest: value - Math.floor(value) }))
      .sort((a, b) => b.rest - a.rest || a.index - b.index)
      .slice(0, short)
      .map((entry) => entry.index);
    const next = floored.map((q, index) => Math.min(MAX_QTY, q + (bonus.includes(index) ? 1 : 0)));

    this.rows.forEach((row, index) => {
      row.dataset.qty = String(next[index]);
    });
  }

  #renderFree() {
    const alloc = this.#allocate();
    const freeUsed = alloc.reduce((sum, a) => sum + a.free, 0);
    const extraCount = alloc.reduce((sum, a) => sum + a.extra, 0);
    const extraTotal = alloc.reduce((sum, a) => sum + a.extra * a.price, 0);

    alloc.forEach(({ row, qty, free, extra, price }) => {
      const value = row.querySelector('[data-upsell-value]');
      if (value) value.textContent = String(qty);
      row.querySelector('[data-upsell-minus]')?.toggleAttribute('disabled', qty <= 0);
      row.querySelector('[data-upsell-plus]')?.toggleAttribute('disabled', qty >= MAX_QTY);
      row.classList.toggle('is-active', qty > 0);

      const priceEl = row.querySelector('[data-upsell-row-price]');
      if (!priceEl) return;
      priceEl.dataset.state = qty === 0 ? '' : extra === 0 ? 'free' : free === 0 ? 'extra' : 'mixed';
      if (qty === 0) {
        priceEl.textContent = '';
      } else if (extra === 0) {
        priceEl.textContent = this.dataset.labelFree || '';
      } else if (free === 0) {
        priceEl.textContent = (this.dataset.labelExtraRow || '')
          .replace('__EXTRA__', String(extra))
          .replace('__PRICE__', this.#money(price));
      } else {
        priceEl.textContent = (this.dataset.labelMixed || '')
          .replace('__FREE__', String(free))
          .replace('__EXTRA__', String(extra))
          .replace('__PRICE__', this.#money(price));
      }
    });

    if (this.counter) {
      this.counter.textContent = (this.dataset.labelCounter || '')
        .replace('__CHOSEN__', String(Math.min(freeUsed, this.freeQty)))
        .replace('__TOTAL__', String(this.freeQty));
      this.counter.classList.toggle('is-complete', freeUsed >= this.freeQty);
    }

    if (this.foot) {
      if (extraCount > 0) {
        this.foot.textContent = (this.dataset.labelExtra || '')
          .replace('__EXTRA__', String(extraCount))
          .replace('__PRICE__', this.#money(extraTotal));
        this.foot.hidden = false;
      } else {
        this.foot.hidden = true;
      }
    }
  }

  #renderAddon(addon) {
    const qty = this.#qty(addon);
    const price = parseInt(addon.dataset.price, 10) || 0;
    const wasActive = addon.classList.contains('is-active');
    addon.classList.toggle('is-active', qty > 0);
    // De pill en de stepper wisselen elkaar af; verplaats de focus mee zodat
    // toetsenbordgebruikers niet op <body> belanden. Het vinkje blijft gewoon
    // staan, dus daar houdt de focus zijn plek.
    const focused = document.activeElement;
    if (focused && addon.contains(focused) && !focused.matches('[data-upsell-toggle]')) {
      if (!wasActive && qty > 0) addon.querySelector('[data-upsell-plus]')?.focus();
      if (wasActive && qty === 0) addon.querySelector('[data-upsell-addon-add]')?.focus();
    }
    addon.querySelector('[data-upsell-toggle]')?.setAttribute('aria-pressed', qty > 0 ? 'true' : 'false');
    addon.querySelector('[data-upsell-minus]')?.toggleAttribute('disabled', qty <= 0);
    addon.querySelector('[data-upsell-plus]')?.toggleAttribute('disabled', qty >= MAX_QTY);
    const value = addon.querySelector('[data-upsell-value]');
    if (value) value.textContent = String(qty);

    const now = addon.querySelector('.oppie-upsell-addon__now');
    if (!now) return;
    if (qty >= 2) {
      now.textContent = (this.dataset.labelMulti || '')
        .replace('__COUNT__', String(qty))
        .replace('__PRICE__', this.#money(price));
    } else {
      now.textContent = `+ ${this.#money(price)}`;
    }
  }

  /**
   * Alle extra regels die mee moeten: gratis-rijen en add-ons met een aantal.
   * Elke regel krijgt verborgen eigenschappen (underscore = niet zichtbaar in
   * wagen en checkout) zodat de order laat zien wat via welke upsell kwam.
   */
  #extraItems() {
    const host = this.dataset.host || '';
    const items = [];
    this.rows.forEach((row) => {
      const qty = this.#qty(row);
      if (qty > 0 && row.dataset.variantId) {
        items.push({
          id: Number(row.dataset.variantId),
          quantity: qty,
          properties: { _oppie_upsell: 'pdp-gratis', _oppie_upsell_bij: host },
        });
      }
    });
    this.addons.forEach((addon) => {
      const qty = this.#qty(addon);
      if (qty > 0 && addon.dataset.variantId) {
        items.push({
          id: Number(addon.dataset.variantId),
          quantity: qty,
          properties: { _oppie_upsell: 'pdp-addon', _oppie_upsell_bij: host },
        });
      }
    });
    return items;
  }

  #onSubmit = (event) => {
    if (!this.form || event.target !== this.form) return;
    const form = this.form;
    if (form.dataset.oppieUpsellBusy === 'true') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const extras = this.#extraItems();
    if (extras.length === 0) return; // niets gekozen: Horizon regelt het zelf

    const mainButton = form.querySelector('button[name="add"], button[type="submit"]');
    if (mainButton?.disabled) return; // uitverkocht: laat Horizon's gedrag staan

    // Zelfde checks als Horizon: aantal-regels en het echt gekozen variant-id.
    const component = form.closest('product-form-component');
    const validation = component?.refs?.quantitySelector?.canAddToCart?.();
    if (validation && !validation.canAdd) return;

    const mainId = Number(new URL(window.location.href).searchParams.get('variant') || formData(form).get('id'));
    if (!mainId) return;
    const mainQty = Number(formData(form).get('quantity')) || 1;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    component?.refs?.addToCartButtonContainer?.animateAddToCart?.();
    this.#submit([{ id: mainId, quantity: mainQty }, ...extras], form, mainButton);
  };

  async #submit(items, form, mainButton) {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add';
    const cartUrl = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
    const body = JSON.stringify({ items, sections: sectionIds.join(',') });

    this.#showError('');
    form.dataset.oppieUpsellBusy = 'true';
    if (mainButton) mainButton.disabled = true;

    try {
      const response = await fetch(addUrl, fetchConfig('json', { body }));
      const result = await response.json();

      if (result.status) {
        this.#showError(result.description || result.message || this.dataset.error || '');
        form.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', result.message, result.description, result.errors));
        return;
      }

      let cart;
      try {
        cart = await (await fetch(`${cartUrl}.js`)).json();
      } catch {
        /* niet blokkerend */
      }

      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'oppie-pdp-upsell', {
          source: 'oppie-pdp-upsell',
          variantId: String(items[0].id),
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: result.sections,
        })
      );
      this.#reset();
    } catch (error) {
      console.error('Oppie upsell add failed:', error);
      this.#showError(this.dataset.error || '');
    } finally {
      delete form.dataset.oppieUpsellBusy;
      if (mainButton) mainButton.disabled = false;
    }
  }

  #showError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = !message;
  }
}

if (!customElements.get('oppie-pdp-upsell')) {
  customElements.define('oppie-pdp-upsell', OppiePdpUpsell);
}
